import BusinessInformation from '@/components/onboarding/BusinessInformation/BusinessInformation';
import SubmitOnboardingFormButton from '@/components/onboarding/SubmitOnboardingFormButton';
import { useGetPharmacyOnboarding } from '@/hooks/onboarding/useGetPharmacyOnboarding';
import type { HttpStatus } from '@montugroup/apis-styleguide';
import type { apiPharmacyContract } from '@montugroup/circuit-api-contracts';
import type { PharmacyOnboardOperatingHoursData, PharmacyOnboardWeekdayKey } from '@montugroup/circuit-shared';
import {
  PharmacyOnboardContactDetails,
  PharmacyOnboardEmailPreferences,
  PharmacyOperatingHours,
} from '@montugroup/circuit-shared/components/index';
import type { Address } from '@montugroup/circuit-shared/types/address';
import { buildOperatingHours } from '@montugroup/circuit-shared/utils/pharmacyOnboarding';
import { Box, Stack } from '@montugroup/design-system';
import type { ClientInferResponseBody } from '@ts-rest/core';
import { Info } from 'luxon';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

export type PharmacyOnboardFormData = {
  pharmacyTradingName: string;
  abn: string;
  fullAddress: Address;
  operatingHours: PharmacyOnboardOperatingHoursData;
  emailPreferences: {
    financialEmail: string;
    communicationEmail: string;
    subscribe: boolean;
  };
  proprietor: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    ahpraNumber: string | null;
  };
  pharmacist: {
    firstName: string;
    lastName: string;
    phone: string;
  };
};

const mapOnboardingDataToForm = (
  data: ClientInferResponseBody<typeof apiPharmacyContract.getPharmacyOnboarding, typeof HttpStatus.OK>['data'],
) => {
  const [proprietorFirstName, proprietorLastName] = (data?.businessContactPerson ?? '').split('$$$$');
  const [pharmacistFirstName, pharmacistLastName] = (data?.pharmacistName ?? '').split('$$$$');
  const [addressLine1, addressLine2] = (data.address ?? '').split('$$$$');
  return {
    pharmacyTradingName: data?.pharmacyName ?? '',
    abn: data?.abn ?? '',
    fullAddress: {
      addressLine1,
      addressLine2,
      suburb: data?.city ?? '',
      state: data?.state ?? '',
      postcode: data?.zipCode ?? '',
    },
    operatingHours: buildOperatingHours(data.openingHours, true),
    emailPreferences: {
      financialEmail: data?.financeEmail ?? '',
      communicationEmail: data?.dayToDayEmail ?? '',
      subscribe: data?.abn ? data?.acceptsMarketing ?? false : true, // default to true unless already filled out
    },
    proprietor: {
      firstName: proprietorFirstName,
      lastName: proprietorLastName,
      phone: data?.businessContactPhone ?? '',
      email: data?.businessContactEmail ?? '',
      ahpraNumber: data?.pharmacistAhpraNumber ?? null,
    },
    pharmacist: {
      firstName: pharmacistFirstName,
      lastName: pharmacistLastName,
      phone: data?.pharmacistPhone ?? '',
    },
  };
};

const OnboardingForm = () => {
  const { token } = useParams();
  const { data, isLoading } = useGetPharmacyOnboarding({ token });

  const values = useMemo(() => (data?.body ? mapOnboardingDataToForm(data.body.data) : undefined), [data]);

  const methods = useForm<PharmacyOnboardFormData>({
    values,
    defaultValues: {
      pharmacyTradingName: '',
      abn: '',
      fullAddress: {},
      operatingHours: Info.weekdays('short')
        .map((day) => day.toLowerCase())
        .reduce((acc, curr) => {
          acc[curr as PharmacyOnboardWeekdayKey] = {
            openingTime: null,
            closingTime: null,
            dayToggled: false,
          };
          return acc;
        }, {} as PharmacyOnboardOperatingHoursData),
      proprietor: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
      },
      pharmacist: {
        firstName: '',
        lastName: '',
        phone: '',
      },
      emailPreferences: {
        financialEmail: '',
        communicationEmail: '',
        subscribe: true,
      },
    },
  });

  return (
    <FormProvider {...methods}>
      <Stack gap={8} marginBottom={8}>
        <BusinessInformation />
        <PharmacyOperatingHours isLoading={isLoading} />
        <PharmacyOnboardContactDetails isLoading={isLoading} />
        <Box sx={{ '& .Mui-checked': { color: (theme) => theme.palette.secondary.main } }}>
          <PharmacyOnboardEmailPreferences isLoading={isLoading} />
        </Box>
        <SubmitOnboardingFormButton text="Submit" path="success" />
      </Stack>
    </FormProvider>
  );
};

export default OnboardingForm;
